import * as THREE from "three";
import { TURRET, turretPivot, turretTurn, type Grid, type TurretState } from "@dyson/shared";

const barrelGeometry = new THREE.BoxGeometry(TURRET.barrel, 0.32, 0.3).translate(TURRET.barrel / 2, 0, 0);
const muzzleGeometry = new THREE.BoxGeometry(0.22, 0.44, 0.34).translate(TURRET.barrel, 0, 0);
const barrelMaterial = new THREE.MeshBasicMaterial({ color: 0x3e4247 });
const muzzleMaterial = new THREE.MeshBasicMaterial({ color: 0x2a2d31 });

// A harpoon launcher: a timber stock bound in iron, the harpoon's shaft lying in it and its barbed head out past the end.
const stockGeometry = new THREE.BoxGeometry(TURRET.barrel * 0.85, 0.46, 0.3).translate((TURRET.barrel * 0.85) / 2, 0, 0);
const bandGeometry = new THREE.BoxGeometry(0.14, 0.54, 0.34);
const shaftGeometry = new THREE.BoxGeometry(TURRET.barrel * 0.75, 0.1, 0.2).translate(TURRET.barrel * 0.55, 0, 0.18);
const headGeometry = new THREE.ShapeGeometry(
  (() => {
    const s = new THREE.Shape();
    const x = TURRET.barrel * 0.92;
    s.moveTo(x + 0.75, 0);
    s.lineTo(x, 0.26);
    s.lineTo(x + 0.12, 0.08);
    s.lineTo(x - 0.25, 0.22);
    s.lineTo(x - 0.1, 0);
    s.lineTo(x - 0.25, -0.22);
    s.lineTo(x + 0.12, -0.08);
    s.lineTo(x, -0.26);
    s.closePath();
    return s;
  })(),
).translate(0, 0, 0.2);
const stockMaterial = new THREE.MeshBasicMaterial({ color: 0x6a4a2c });
const bandMaterial = new THREE.MeshBasicMaterial({ color: 0x2f3236 });
const steelMaterial = new THREE.MeshBasicMaterial({ color: 0xb4bcc4 });

/**
 * The swinging gun barrels of the turrets (and launchers of the harpoons) on
 * one ship or asteroid, in its grid's coordinates (add `group` wherever the
 * grid is drawn).
 */
export class TurretBarrels {
  readonly group = new THREE.Group();
  /** One barrel per turret, by the turret's anchor index, and the quarter turns each turret is turned by (its aim is in its own frame). */
  private readonly barrels = new Map<number, THREE.Group>();
  private readonly turns = new Map<number, number>();

  /** Harpoons' heads, by anchor: shown while one's loaded. */
  private readonly heads = new Map<number, THREE.Object3D>();

  /** Have a barrel on exactly these turrets, and a launcher on these harpoons (anchor indices into `grid`). */
  set(grid: Grid, anchors: Iterable<number>, harpoons: Iterable<number> = []): void {
    const guns = new Set(anchors);
    const keep = new Set([...guns, ...harpoons]);
    for (const anchor of [...this.barrels.keys()]) if (!keep.has(anchor) || guns.has(anchor) === this.heads.has(anchor)) this.remove(anchor);
    for (const anchor of keep) this.add(grid, anchor, !guns.has(anchor));
  }

  /** A barrel for the turret (or launcher for the harpoon) anchored at `anchor`, pivoting on the middle of its housing. */
  add(grid: Grid, anchor: number, harpoon = false): void {
    if (this.barrels.has(anchor)) return;
    const barrel = new THREE.Group();
    if (harpoon) {
      const head = new THREE.Group();
      head.add(new THREE.Mesh(shaftGeometry, steelMaterial), new THREE.Mesh(headGeometry, steelMaterial));
      barrel.add(new THREE.Mesh(stockGeometry, stockMaterial), head);
      for (const x of [0.6, TURRET.barrel * 0.7]) {
        const band = new THREE.Mesh(bandGeometry, bandMaterial);
        band.position.x = x;
        barrel.add(band);
      }
      this.heads.set(anchor, head);
    } else barrel.add(new THREE.Mesh(barrelGeometry, barrelMaterial), new THREE.Mesh(muzzleGeometry, muzzleMaterial));
    const p = turretPivot(grid, anchor);
    const turn = (turretTurn(grid, anchor) * Math.PI) / 2;
    barrel.position.set(p.x, p.y, 0.3);
    barrel.rotation.z = Math.PI / 2 + turn;
    this.group.add(barrel);
    this.barrels.set(anchor, barrel);
    this.turns.set(anchor, turn);
  }

  remove(anchor: number): void {
    const barrel = this.barrels.get(anchor);
    if (!barrel) return;
    this.group.remove(barrel);
    this.barrels.delete(anchor);
    this.turns.delete(anchor);
    this.heads.delete(anchor);
  }

  has(anchor: number): boolean {
    return this.barrels.has(anchor);
  }

  get size(): number {
    return this.barrels.size;
  }

  /** Point each barrel where its turret is aiming; the harpoon whose line is out (`out`, its anchor) has none loaded. */
  aim(turrets: Record<string, TurretState> | undefined, out: number | null = null): void {
    for (const [anchor, barrel] of this.barrels) {
      const aim = turrets?.[anchor]?.aim;
      if (aim !== undefined) barrel.rotation.z = aim + (this.turns.get(anchor) ?? 0);
    }
    for (const [anchor, head] of this.heads) head.visible = anchor !== out;
  }
}
